
import { useState } from 'react';

const catalogItems = [
  {
    id: 1,
    title: 'Optical Sensor Smart Digital Water Meter Catalog',
    description:
      'Comprehensive catalog including product specifications, technical features, and installation guide for the 3rd generation optical sensor smart water meter.',
    icon: 'ri-file-pdf-2-line',
    pages: '24 Pages',
    size: '8.5MB',
    tag: 'Product Catalog',
    gradient: 'from-teal-600 to-cyan-600',
  },
  {
    id: 2,
    title: 'Technical Specifications & Certifications',
    description:
      'Technical documentation including detailed optical sensing technology specifications, patent information, KC certification, and various test reports.',
    icon: 'ri-file-text-line',
    pages: '16 Pages',
    size: '5.2MB',
    tag: 'Technical Document',
    gradient: 'from-cyan-600 to-teal-600',
  },
  {
    id: 3,
    title: 'Business Proposal (Government & Utilities)',
    description:
      'Smart water meter adoption proposal for local governments and water utilities, including cost reduction effects and implementation cases.',
    icon: 'ri-slideshow-line',
    pages: '32 Pages',
    size: '12.3MB',
    tag: 'Business Proposal',
    gradient: 'from-teal-700 to-emerald-600',
  },
];

export default function CatalogDownload() {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState<typeof catalogItems[0] | null>(null);
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleDownloadClick = (catalog: typeof catalogItems[0]) => {
    setSelectedCatalog(catalog);
    setShowModal(true);
    setSubmitted(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCatalog) return;

    setDownloadingId(selectedCatalog.id);

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('company', formData.company);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('catalog', selectedCatalog.title);

      const response = await fetch('https://readdy.ai/api/form/d648iqdanburc340qq70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok (${response.status})`);
      }

      setSubmitted(true);
      setTimeout(() => {
        setDownloadingId(null);
        setShowModal(false);
        setFormData({ name: '', company: '', email: '', phone: '' });
        setSubmitted(false);
      }, 2500);
    } catch (error) {
      console.error('Download request failed:', error);
      setDownloadingId(null);
    }
  };

  return (
    <section id="catalog" className="py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-teal-50 rounded-full blur-[120px] opacity-60"></div>
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-cyan-50 rounded-full blur-[100px] opacity-60"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block mb-4 px-4 md:px-6 py-2 bg-teal-50 border border-teal-200 rounded-full">
            <span className="text-teal-600 font-semibold tracking-wider text-xs md:text-sm uppercase">
              Catalog
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            Product <span className="text-teal-600">Catalog Download</span>
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Download ROBO Asset Grid&apos;s optical sensor smart water meter product catalogs and technical documents
          </p>
        </div>

        {/* Catalog Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
          {catalogItems.map((catalog) => (
            <div
              key={catalog.id}
              className="group relative bg-white rounded-2xl border border-gray-200 hover:border-teal-300 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden"
            >
              {/* Top Gradient Bar */}
              <div className={`h-1.5 bg-gradient-to-r ${catalog.gradient}`}></div>

              <div className="p-5 md:p-8">
                {/* Tag */}
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <span className="px-3 py-1 bg-teal-50 text-teal-600 text-[10px] md:text-xs font-semibold rounded-full">
                    {catalog.tag}
                  </span>
                  <div className="flex items-center gap-2 text-[10px] md:text-xs text-gray-400">
                    <span>{catalog.pages}</span>
                    <span>·</span>
                    <span>{catalog.size}</span>
                  </div>
                </div>

                {/* Icon */}
                <div
                  className={`w-14 md:w-18 h-14 md:h-18 bg-gradient-to-br ${catalog.gradient} rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}
                >
                  <i className={`${catalog.icon} text-2xl md:text-3xl text-white`}></i>
                </div>

                {/* Content */}
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors duration-300 leading-snug">
                  {catalog.title}
                </h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-5 md:mb-8">{catalog.description}</p>

                {/* Download Button */}
                <button
                  onClick={() => handleDownloadClick(catalog)}
                  disabled={downloadingId === catalog.id}
                  className={`w-full flex items-center justify-center gap-2 px-5 py-3 md:py-3.5 bg-gradient-to-r ${catalog.gradient} text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed`}
                >
                  {downloadingId === catalog.id ? (
                    <>
                      <i className="ri-loader-4-line text-lg animate-spin"></i>
                      <span>Preparing Download...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-download-2-line text-lg"></i>
                      <span>Download Catalog</span>
                    </>
                  )}
                </button>
              </div>

              {/* Hover Glow */}
              <div
                className={`absolute -bottom-16 -right-16 w-32 h-32 bg-gradient-to-br ${catalog.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-10 transition-all duration-500`}
              ></div>
            </div>
          ))}
        </div>

        {/* Bottom Notice */}
        <div className="mt-10 md:mt-14 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-3 bg-gray-50 rounded-full border border-gray-200">
            <i className="ri-information-line text-teal-600 text-lg"></i>
            <span className="text-xs md:text-sm text-gray-600">
              Simple information input is required for catalog download. Your information will only be used for document
              delivery purposes.
            </span>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      {showModal && selectedCatalog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowModal(false);
              setSubmitted(false);
            }}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 md:p-8 animate-[fadeInUp_0.3s_ease-out]">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowModal(false);
                setSubmitted(false);
              }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {submitted ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Download Starting</h3>
                <p className="text-sm text-gray-600">We will also send the catalog to your email.</p>
              </div>
            ) : (
              <>
                {/* Modal Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-10 h-10 bg-gradient-to-br ${selectedCatalog.gradient} rounded-lg flex items-center justify-center`}
                    >
                      <i className={`${selectedCatalog.icon} text-lg text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{selectedCatalog.title}</h3>
                      <p className="text-xs text-gray-500">
                        {selectedCatalog.pages} · {selectedCatalog.size}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Please enter the information below for download.</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="catalog-download-form-en">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company/Organization</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Company or organization name"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="example@company.com"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1-234-567-8900"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap mt-2"
                  >
                    <i className="ri-download-2-line text-lg"></i>
                    <span>Download Catalog</span>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
